import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { Calendar, Clock, Book, CheckCircle, ArrowRight, Play, User, MapPin, ExternalLink, Lock } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import MarkdownContent from '../../components/MarkdownContent'

export default function DailySchedule() {
  const [schedule, setSchedule] = useState(null)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => {
    fetchSchedule()
  }, [selectedDate])

  const fetchSchedule = async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/schedule/daily/${selectedDate}`)
      setSchedule(data)
      setEvents(data.events || [])
    } catch (error) {
      console.error('Error fetching schedule:', error)
      toast.error('Failed to load schedule')
    } finally {
      setLoading(false)
    }
  }

  const getLessonTypeColor = (type) => {
    switch (type) {
      case 'theory': return 'bg-blue-100 text-blue-800'
      case 'practical': return 'bg-green-100 text-green-800'
      case 'project': return 'bg-purple-100 text-purple-800'
      case 'assessment': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'video': return '🎥'
      case 'presentation': return '📊'
      case 'assignment': return '📝'
      case 'quiz': return '❓'
      default: return '📄'
    }
  }

  const lessonTypeCls = {
    theory:     'bg-blue-500/10 text-blue-400 border-blue-500/25',
    practical:  'bg-green-500/10 text-green-400 border-green-500/25',
    project:    'bg-purple-500/10 text-purple-400 border-purple-500/25',
    assessment: 'bg-cinema-glow/10 text-cinema-glow border-cinema-glow/25',
  }
  const eventTypeCls = {
    class:   'bg-blue-500/10 text-blue-400 border-blue-500/25',
    exam:    'bg-red-500/10 text-red-400 border-red-500/25',
    meeting: 'bg-green-500/10 text-green-400 border-green-500/25',
    holiday: 'bg-purple-500/10 text-purple-400 border-purple-500/25',
  }
  const eventBarCls = { class: 'bg-blue-500', exam: 'bg-red-500', meeting: 'bg-green-500', holiday: 'bg-purple-500' }

  if (loading) return <LoadingSpinner label="Loading schedule" />

  if (!schedule || ((schedule.schedules?.length || 0) === 0 && events.length === 0)) {
    return (
      <div className="cinema-empty">
        <Calendar size={36} className="mx-auto mb-4 text-cinema-muted/20" />
        <h3 className="font-display text-base text-white font-light mb-2">No Active Courses</h3>
        <p className="font-sans text-xs text-cinema-muted">You don't have any active course enrollments yet.</p>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">My Daily Schedule</h1>
        </div>
      </div>

      {/* Scheduled Events */}
      {events.length > 0 && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header flex items-center justify-between">
            <h2 className="cinema-section-title">Scheduled Events</h2>
            <div className="flex items-center gap-2">
              <Calendar size={13} className="text-cinema-muted" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-xs font-sans px-3 py-1.5 focus:outline-none focus:border-cinema-glow/40 transition-colors"
              />
            </div>
          </div>
          <div className="divide-y divide-white/4">
            {events.map((event) => (
              <div key={event.id} className="flex items-center gap-4 py-4 hover:bg-white/[0.02] transition-colors">
                <div className="text-center min-w-[64px]">
                  <p className="font-mono text-xs sm:text-sm text-white font-medium">{new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  <p className="font-mono text-xs text-cinema-muted">{new Date(event.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className={`w-0.5 h-8 rounded-full ${eventBarCls[event.event_type] || 'bg-white/20'}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-white font-medium truncate">{event.title}</p>
                  {event.course_name && <p className="font-sans text-xs text-cinema-muted mt-0.5">{event.course_code} — {event.course_name}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${eventTypeCls[event.event_type] || 'bg-white/5 text-cinema-muted border-white/10'}`}>
                    {event.event_type}
                  </span>
                  {event.location && (
                    <span className="flex items-center gap-1 font-sans text-xs text-cinema-muted">
                      <MapPin size={11} />{event.location}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Course schedules */}
      {schedule.schedules.map((cs) => {
        const pct = cs.total_lessons > 0 ? Math.round((cs.completed_lessons / cs.total_lessons) * 100) : 0
        return (
          <div key={`${cs.student_id}_${cs.course_id}`} className="pt-8 border-t border-white/5">
            {/* Course header */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="min-w-0">
                <h2 className="font-display text-lg text-white font-normal truncate">{cs.course_title}</h2>
                <p className="cinema-label mt-0.5">{cs.course_code}</p>
                {cs.trainer && (
                  <p className="flex items-center gap-1.5 font-sans text-xs sm:text-sm text-cinema-muted mt-1.5 font-medium">
                    <User size={13} /> {cs.trainer.full_name}{cs.trainer.specialization && ` · ${cs.trainer.specialization}`}
                  </p>
                )}
                {cs.enrollment_date && (
                  <p className="font-sans text-xs text-cinema-muted/80 mt-1">
                    Enrolled {Math.floor((Date.now() - new Date(cs.enrollment_date)) / 86400000)}d ago · {cs.completed_lessons}/{cs.total_lessons} lessons
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="cinema-label mb-1">Progress</p>
                <p className="font-display text-2xl text-cinema-glow font-semibold">{pct}%</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Payment lock banner */}
              {cs.payment_locked && (
                <div className="flex items-center gap-3 px-4 py-3 bg-yellow-500/10 border border-yellow-500/25 rounded-sm">
                  <Lock size={16} className="text-yellow-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-xs text-yellow-400 font-medium">
                      Course content is locked. Pay at least 50% of tuition to start accessing lessons.
                    </p>
                    {cs.payment_progress && (
                      <p className="font-sans text-xs text-cinema-muted mt-0.5">
                        {cs.payment_progress.percent_paid}% paid · Balance: {Math.round(cs.payment_progress.balance_due || 0).toLocaleString()} UGX
                      </p>
                    )}
                  </div>
                  <Link
                    to="/student/payments"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans font-medium cinema-btn-primary shrink-0"
                  >
                    Pay Tuition
                  </Link>
                </div>
              )}

              {/* Current lesson */}
              {cs.current_lesson && (
                <div>
                  <p className="flex items-center gap-2 cinema-label text-cinema-glow mb-3"><Play size={13} /> Currently In Progress</p>
                  <div className="pl-4 border-l border-cinema-glow/30">
                    <div className="flex items-start gap-3">
                      <span className="text-xl shrink-0">{getContentTypeIcon(cs.current_lesson.content_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-base text-white font-medium">{cs.current_lesson.lesson_title}</p>
                        <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">{cs.current_lesson.module_title} · Week {cs.current_lesson.week_number}</p>
                        {cs.current_lesson.lesson_description && <MarkdownContent content={cs.current_lesson.lesson_description} compact />}
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="flex items-center gap-1 font-sans text-xs text-cinema-muted"><Clock size={12} /> {cs.current_lesson.duration_minutes} mins</span>
                          <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${lessonTypeCls[cs.current_lesson.lesson_type] || 'bg-white/5 text-cinema-muted border-white/10'}`}>{cs.current_lesson.lesson_type}</span>
                          <span className="px-2.5 py-0.5 text-xs font-sans font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/25 rounded-sm">Day {cs.current_lesson.day_number}</span>
                        </div>
                        {/* Continue Lesson button — locked if payment not met */}
                        {cs.current_lesson.is_unlocked === false ? (
                          <span className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-xs font-sans font-medium cinema-glass border border-yellow-500/25 text-yellow-400 min-h-[36px]">
                            <Lock size={13} /> Locked — Pay to Unlock
                          </span>
                        ) : (
                          <Link
                            to={`/student/courses/${cs.course_id}?lesson=${cs.current_lesson.lesson_id}`}
                            className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-xs font-sans font-medium cinema-btn-primary min-h-[36px]"
                          >
                            <Play size={13} /> Continue Lesson
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Next lesson */}
              {cs.next_lesson && (
                <div>
                  <p className="flex items-center gap-2 cinema-label text-green-400 mb-3"><ArrowRight size={13} /> {cs.current_lesson ? 'Up Next' : "Today's Lesson"}</p>
                  <div className="pl-4 border-l border-green-500/30">
                    <div className="flex items-start gap-3">
                      <span className="text-xl shrink-0">{getContentTypeIcon(cs.next_lesson.content_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-base text-white font-medium">{cs.next_lesson.lesson_title}</p>
                        <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">{cs.next_lesson.module_title} · Week {cs.next_lesson.week_number}</p>
                        {cs.next_lesson.lesson_description && <MarkdownContent content={cs.next_lesson.lesson_description} compact />}
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="flex items-center gap-1 font-sans text-xs text-cinema-muted"><Clock size={12} /> {cs.next_lesson.duration_minutes} mins</span>
                          <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${lessonTypeCls[cs.next_lesson.lesson_type] || 'bg-white/5 text-cinema-muted border-white/10'}`}>{cs.next_lesson.lesson_type}</span>
                          <span className="px-2.5 py-0.5 text-xs font-sans font-medium bg-blue-500/10 text-blue-400 border border-blue-500/25 rounded-sm">Day {cs.next_lesson.day_number}</span>
                        </div>
                        {/* Open Lesson button — locked if payment not met */}
                        {cs.next_lesson.is_unlocked === false ? (
                          <span className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-xs font-sans font-medium cinema-glass border border-yellow-500/25 text-yellow-400 min-h-[36px]">
                            <Lock size={13} /> Locked — Pay to Unlock
                          </span>
                        ) : (
                          <Link
                            to={`/student/courses/${cs.course_id}?lesson=${cs.next_lesson.lesson_id}`}
                            className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-xs font-sans font-medium cinema-glass border border-green-500/25 text-green-400 hover:bg-green-500/10 transition-all min-h-[36px]"
                          >
                            <ExternalLink size={13} /> Open Lesson
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Upcoming lessons */}
              {cs.upcoming_lessons.length > 0 && (
                <div>
                  <p className="flex items-center gap-2 cinema-label mb-3"><Book size={12} /> Coming Up</p>
                  <div className="cinema-list">
                    {cs.upcoming_lessons.map((lesson) => {
                      const locked = lesson.is_unlocked === false;
                      const inner = (
                        <>
                          <span className="text-lg shrink-0 mt-0.5">{locked ? <Lock size={16} className="text-yellow-400/60" /> : getContentTypeIcon(lesson.content_type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-sans text-sm font-medium truncate ${locked ? 'text-cinema-muted/60' : 'text-white group-hover:text-cinema-glow transition-colors'}`}>{lesson.lesson_title}</p>
                            <p className="font-sans text-xs text-cinema-muted mt-0.5">{lesson.module_title} · Week {lesson.week_number}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {locked ? (
                              <span className="px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border border-yellow-500/20 text-yellow-400/60 rounded-sm">Locked</span>
                            ) : (
                              <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${lessonTypeCls[lesson.lesson_type] || 'bg-white/5 text-cinema-muted border-white/10'}`}>{lesson.lesson_type}</span>
                            )}
                            <span className="font-sans text-xs text-cinema-muted font-medium">Day {lesson.day_number}{lesson.duration_minutes ? ` · ${lesson.duration_minutes}m` : ''}</span>
                          </div>
                        </>
                      );
                      return locked ? (
                        <div key={lesson.lesson_id} className="cinema-list-row opacity-60">
                          {inner}
                        </div>
                      ) : (
                        <Link
                          key={lesson.lesson_id}
                          to={`/student/courses/${cs.course_id}?lesson=${lesson.lesson_id}`}
                          className="cinema-list-row group hover:border-cinema-glow/20 transition-all"
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* All caught up */}
              {!cs.current_lesson && !cs.next_lesson && cs.upcoming_lessons.length === 0 && (
                <div className="text-center py-8">
                  <CheckCircle size={32} className="mx-auto mb-3 text-green-400" />
                  <p className="font-display text-base text-white font-light mb-1">All Caught Up!</p>
                  <p className="font-sans text-sm text-cinema-muted">You've completed all available lessons in this course.</p>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
